import { useEffect, useMemo } from "react";
import { useForm } from "react-hook-form";
import {
  BUTTON_LABELS,
  COST_CALCULATION_TYPE,
  PERMISSIONS,
} from "@miplatform/constants";
import { useRouter } from "@miplatform/hooks";
import { zodResolver } from "@hookform/resolvers/zod";
import { VerticalTabsLayout } from "@mibuildweb/components";
import { usePermissions } from "@mibuildweb/hooks";
import { useTheme } from "@mui/material";
import { ConfigPageLayout } from "../../../../Config/ConfigPageLayout";
import { addCategoriesSchema } from "../schema/addCategories.schema";
import { CategoryDetailTab } from "../tabs/CategoryDetailTab/CategoryDetailTab";
import { CategoryPatternTab } from "../tabs/CategoryPatternTab/CategoryPatternTab";
import { useAddCategoriesViewModel } from "./useAddCategoriesViewModel";

export function AddCategories() {
  const { params, goBack } = useRouter();
  const { id: categoryId } = params;
  const theme = useTheme();
  const { hasPermission } = usePermissions();

  const isReadOnly = !hasPermission(PERMISSIONS.MATERIAL_CATEGORY.UPDATE);

  const form = useForm({
    mode: "onChange",
    defaultValues: {
      name: "",
      imageRequired: false,
      attributes: [],
      displayOrder: null,
      linkedMaterials: [],
      itemCodeNumberPattern: "",
      itemDescriptionPattern: "{CategoryName}",
      costCalculation: COST_CALCULATION_TYPE.COST_PLUS,
      dropRequired: false,
    },
    resolver: zodResolver(addCategoriesSchema),
  });

  const {
    formState: { isValid, isDirty },
    reset,
    getValues,
    watch,
  } = form;

  const { name } = watch();

  const {
    fetchCategoryDetails,
    handleCreateCategory,
    handleUpdateCategory,
    isLoading,
  } = useAddCategoriesViewModel({ reset, goBack });

  useEffect(() => {
    if (categoryId) {
      fetchCategoryDetails(categoryId);
    }
  }, []);

  const tabs = useMemo(
    () => [
      {
        label: "Details",
        value: "details",
        controlledFormViewComponent: CategoryDetailTab,
        isComplete: isValid,
        hasErrors: !isValid,
        extraTabProps: { isReadOnly },
      },
      {
        label: "Layout & Preview",
        value: "layout & preview",
        controlledFormViewComponent: CategoryPatternTab,
        extraTabProps: { isReadOnly },
      },
    ],
    [isValid]
  );

  const handleSubmit = () => {
    const data = getValues();
    if (categoryId) {
      handleUpdateCategory(data);
    } else {
      handleCreateCategory(data);
    }
  };

  const renderTitle = () => {
    const action = categoryId ? "Edit" : "Add";
    return (
      <>
        {action} Category:{" "}
        <span style={{ color: theme.palette.primary.main }}>{name}</span>
      </>
    );
  };

  const actions = {
    cancel: {
      label: BUTTON_LABELS.CANCEL,
      onClick: () => {
        goBack();
      },
    },
    submit: {
      label: categoryId ? "Update Category" : "Save Category",
      onClick: () => {
        handleSubmit();
      },
      disabled: isLoading || !isValid || !isDirty,
    },
  };

  return (
    <ConfigPageLayout
      renderTitle={renderTitle}
      bottomActions={actions}
      loading={isLoading}
    >
      <VerticalTabsLayout tabs={tabs} form={form} transparentBackground />
    </ConfigPageLayout>
  );
}
